import React, { useState } from 'react';
// Kein Loader2 mehr: der Spinner kommt aus `Button loading=…`, das `icon`
// daneben wurde von Button waehrend des Ladens ohnehin nie gezeichnet.
import { AlertTriangle, CalendarDays, Receipt } from 'lucide-react';
import { Button } from '../Button';
import {
    EINZELN,
    LinkedBillingMode,
    TAGESPAUSCHALE,
    billingModeLabelDe,
    heute,
} from '../../services/linkedBillingMode';

interface Props {
    /** Die heute geltende Art. */
    aktuell: LinkedBillingMode;
    mutterName: string;
    /** Wirft nie — der Text der Rueckmeldung landet im Dialog. */
    onSwitch: (neu: LinkedBillingMode) => Promise<{ ok: boolean; message: string }>;
    onClose: () => void;
}

const TAG_DE = (ymd: string) => ymd.split('-').reverse().join('.');

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * ABRECHNUNGSART DER TOCHTER UMSTELLEN
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Bisher liess sich die Art nur beim Verknuepfen festlegen. Wer sie aendern
 * wollte, musste trennen und neu verknuepfen — und dabei wandern die
 * Leistungen mit (`applyLinkTransfer`), was aus einer Einstellung einen Umbau
 * macht.
 *
 * Der Dialog sagt beide Saetze, die der Bearbeiter braucht, und sagt sie VOR
 * dem Klick:
 *
 *  · was sich aendert — ab wann die neue Art gilt,
 *  · was sich NICHT aendert — kein bestehender Beleg, keine gebuchte Leistung,
 *    keine alte Begehung wird neu beurteilt.
 *
 * Der zweite Satz ist der wichtigere. Die Abrechnungsart entscheidet naemlich
 * rueckwirkend darueber, ob eine alte Begehung als abgerechnet gilt: bei
 * Tagespauschale deckt die Buchung der Mutter am selben Tag die Tochter mit.
 * Ohne die Stichtagsregel machte ein Umschalten auf „einzeln" jede frueher
 * gedeckte Begehung schlagartig wieder zu einer offenen Forderung.
 */
export const BillingModeSwitchDialog: React.FC<Props> = ({ aktuell, mutterName, onSwitch, onClose }) => {
    const [gewaehlt, setGewaehlt] = useState<LinkedBillingMode>(aktuell);
    const [laeuft, setLaeuft] = useState(false);
    const [fehler, setFehler] = useState<string | null>(null);

    const stichtag = heute();
    const geaendert = gewaehlt !== aktuell;

    const ausfuehren = async () => {
        setLaeuft(true);
        setFehler(null);
        const ergebnis = await onSwitch(gewaehlt);
        if (ergebnis.ok) { onClose(); return; }
        setFehler(ergebnis.message);
        setLaeuft(false);
    };

    const Karte = (
        modus: LinkedBillingMode,
        Icon: typeof CalendarDays,
        titel: string,
        text: string,
    ) => (
        <button
            key={modus}
            type="button"
            onClick={() => setGewaehlt(modus)}
            className={`p-3 rounded-xl border text-left transition-all w-full ${
                gewaehlt === modus ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-slate-200 hover:border-slate-300'
            }`}
        >
            <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4 text-slate-600" />
                <span className="font-bold text-sm text-slate-800">{titel}</span>
                {modus === aktuell && (
                    <span className="ml-auto text-[10px] font-bold uppercase tracking-wide text-slate-500 bg-slate-100 border border-slate-200 rounded-full px-2 py-0.5">
                        aktuell
                    </span>
                )}
            </div>
            <p className="text-xs text-slate-500">{text}</p>
        </button>
    );

    return (
        <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-scale-in">
                <div className="p-5 border-b border-slate-100">
                    <h3 className="font-bold text-slate-800">Abrechnung gegenüber dem Mutterprojekt</h3>
                    <p className="text-xs text-slate-500 mt-0.5">Mutterprojekt: {mutterName}</p>
                </div>

                <div className="p-5 space-y-3">
                    {Karte(
                        TAGESPAUSCHALE, CalendarDays, 'Tagespauschale',
                        'Der Kalendertag ist die Einheit. Werden Mutter und Töchter am selben Tag begangen, '
                        + 'ist das EINE Leistung und wird einmal gebucht.',
                    )}
                    {Karte(
                        EINZELN, Receipt, 'Einzelabrechnung',
                        'Jede Tochter hat ihre eigene Leistung. Derselbe Begehungstag deckt nichts — '
                        + 'jede Begehung wird für sich gebucht.',
                    )}

                    {geaendert && (
                        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 flex gap-2 text-sm text-amber-900">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            <div>
                                <p>
                                    Die Umstellung auf <b>{billingModeLabelDe(gewaehlt)}</b> gilt ab{' '}
                                    <b>{TAG_DE(stichtag)}</b> und wirkt nur nach vorn.
                                </p>
                                <p className="mt-1.5">
                                    Unverändert bleiben: alle bestehenden Belege, jede bereits gebuchte
                                    Leistung und die Beurteilung aller Begehungen bis einschließlich{' '}
                                    {TAG_DE(stichtag)} — die werden weiterhin nach{' '}
                                    <b>{billingModeLabelDe(aktuell)}</b> beurteilt.
                                </p>
                            </div>
                        </div>
                    )}

                    {fehler && (
                        <div className="rounded-xl border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800">{fehler}</div>
                    )}
                </div>

                <div className="p-4 border-t border-slate-100 flex justify-end gap-2">
                    <Button variant="ghost" onClick={onClose} disabled={laeuft}>Abbrechen</Button>
                    <Button
                        variant="primary"
                        loading={laeuft}
                        disabled={!geaendert || laeuft}
                        onClick={() => void ausfuehren()}
                    >
                        Umstellen
                    </Button>
                </div>
            </div>
        </div>
    );
};
